import {
  IErrorApiResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import { UseMutationOptions, restrictApi, useMutation } from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { ContainerOrderQueryKeys } from '~/container-order/api';
import { PurchaseErrorCodeKeys } from '~/purchase/types';
import { ISupplierContainerOrderGetOneById } from '~/container-order/types';

type ImportValidationError = IValidationError<
  PurchaseErrorCodeKeys,
  { lineIndex: number }
>;
type ImportSuccessResponse = ISuccessApiResponse<
  { ean: string; quantity: number }[],
  {
    errors: Array<ImportValidationError>;
  } // Non blocking errors
>;
type Options = {
  import?: UseMutationOptions<
    ImportSuccessResponse,
    IErrorApiResponse<ImportValidationError, PurchaseErrorCodeKeys>,
    {
      file: File;
      orderId: string;
      itemId: string;
    }
  >;
  updateLines?: UseMutationOptions<
    ISuccessApiResponse<ISupplierContainerOrderGetOneById>,
    IErrorApiResponse<IValidationError<PurchaseErrorCodeKeys>>,
    {
      orderId: string;
      itemId: string;
      body: {
        lines: Array<{ ean: string; quantity: number; price?: number }>;
      };
    }
  >;
  updateTransportItem?: UseMutationOptions<
    ISuccessApiResponse<ISupplierContainerOrderGetOneById>,
    IErrorApiResponse<
      IValidationError<PurchaseErrorCodeKeys>,
      PurchaseErrorCodeKeys
    >,
    {
      orderId: string;
      itemId: string;
      body: {
        reference?: string;
        deliveryDate: Date;
        status: string;
      };
    }
  >;
  downloadTemplate?: UseMutationOptions<
    void,
    IErrorApiResponse,
    {
      userId: string;
    }
  >;
};

export const useManageTransportItems = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const importMutation = useMutation({
    mutationFn: (variables) => {
      const formData = new FormData();
      formData.append('file', variables.file);
      return callCoreApi<ImportSuccessResponse>(
        `/offers/api/tenants/${tenantCode}/container-orders/${variables.orderId}/items/${variables.itemId}/import`,
        {
          method: 'POST',
          body: formData,
          asJson: false,
        },
      );
    },
    ...options.import,
  });

  const updateLinesMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<ISupplierContainerOrderGetOneById>>(
        `/offers/api/tenants/${tenantCode}/container-orders/${variables.orderId}/items/${variables.itemId}/lines`,
        {
          method: 'PUT',
          body: JSON.stringify(variables.body),
        },
      ),
    ...options.updateLines,
    keysToInvalidateOnSuccess: [ContainerOrderQueryKeys.containerOrders.base()],
  });

  const updateTransportItemMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<ISupplierContainerOrderGetOneById>>(
        `/offers/api/tenants/${tenantCode}/container-orders/${variables.orderId}/items/${variables.itemId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(variables.body),
        },
      ),
    ...options.updateTransportItem,
    keysToInvalidateOnSuccess: [ContainerOrderQueryKeys.containerOrders.base()],
  });

  const downloadTemplateMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<void>(
        `/offers/api/tenants/${tenantCode}/users/${variables.userId}/container-orders/import/template`,
        {
          method: 'GET',
        },
      ),
    ...options.downloadTemplate,
  });

  return restrictApi(options, {
    import: importMutation,
    updateLines: updateLinesMutation,
    updateTransportItem: updateTransportItemMutation,
    downloadTemplate: downloadTemplateMutation,
  });
};
